import { createIcon } from "./icons";

export type ZoomControlPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface ZoomControlOptions {
  /** Coin de l'écran où placer le contrôle. Par défaut : "bottom-right". */
  position?: ZoomControlPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  /** Infobulle et libellé d'accessibilité du bouton "+". Par défaut : "Zoomer". */
  zoomInLabel?: string;
  /** Infobulle et libellé d'accessibilité du bouton "−". Par défaut : "Dézoomer". */
  zoomOutLabel?: string;
}

const POSITION_OFFSETS: Record<ZoomControlPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

/** Encombrement du bloc, bordures comprises — voir `stackOffsetPx`. */
export const ZOOM_CONTROL_HEIGHT_PX = 72;
export const ZOOM_CONTROL_WIDTH_PX = 36;

// Même famille visuelle que `ResetViewButton`/`GeolocationButton`, en rectangle arrondi : les deux
// boutons partagent un seul bloc.
const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
  position: "absolute",
  zIndex: "10",
  display: "flex",
  flexDirection: "column",
  width: "36px",
  background: "#fff",
  border: "1px solid #ddd",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  overflow: "hidden",
};

/**
 * Vrai si un cran de `delta` déplacerait encore la vue. La tolérance absorbe le zoom fractionnaire
 * laissé par un pincement ou un `fitBounds` : sans elle, 21,9999 pour un maximum de 22 laisserait le
 * "+" actif pour un dixième de cran.
 */
export function canZoom(current: number, bounds: { min: number; max: number }, delta: number): boolean {
  const EPSILON = 0.01;
  return delta > 0 ? current < bounds.max - EPSILON : current > bounds.min + EPSILON;
}

/**
 * Boutons "+" / "−" flottants — le seul moyen de zoomer sans molette ni pincement, et le seul
 * endroit où une carte bornée par `setZoomBounds` le fait savoir (le bouton concerné se grise).
 */
export class ZoomControl {
  private el: HTMLDivElement;
  private zoomInButton: HTMLButtonElement;
  private zoomOutButton: HTMLButtonElement;
  private edgeProperty: "top" | "bottom";

  constructor(container: HTMLElement, options: ZoomControlOptions, onZoom: (delta: number) => void) {
    this.el = document.createElement("div");
    this.el.className = "toka-zoom-control";
    Object.assign(this.el.style, DEFAULT_STYLE, POSITION_OFFSETS[options.position ?? "bottom-right"], options.style);
    this.edgeProperty = (options.position ?? "bottom-right").startsWith("top") ? "top" : "bottom";

    this.zoomInButton = createZoomButton("plus", options.zoomInLabel ?? "Zoomer", () => onZoom(1));
    this.zoomOutButton = createZoomButton("minus", options.zoomOutLabel ?? "Dézoomer", () => onZoom(-1));
    this.zoomOutButton.classList.add("toka-zoom-control__button--last");

    this.el.appendChild(this.zoomInButton);
    this.el.appendChild(this.zoomOutButton);
    container.appendChild(this.el);
  }

  /** Grise le bouton dont la borne est atteinte. */
  setZoomState(current: number, bounds: { min: number; max: number }): void {
    this.zoomInButton.disabled = !canZoom(current, bounds, 1);
    this.zoomOutButton.disabled = !canZoom(current, bounds, -1);
  }

  /** Voir `GeolocationButton.setStackOffset`. */
  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    this.el.remove();
  }
}

function createZoomButton(icon: "plus" | "minus", label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "toka-zoom-control__button";
  button.appendChild(createIcon(icon));
  button.setAttribute("aria-label", label);
  button.title = label;
  button.addEventListener("click", onClick);
  return button;
}
